import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api, { errMsg, fileUrl } from "@/lib/api";
import { clienteDisplayName, clienteSecondaryName } from "@/lib/clienteDisplay";
import AmpliarMixModal from "@/components/AmpliarMixModal";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import Crumbs from "@/components/Crumbs";
import { saveCurrentScroll, useScrollRestore } from "@/lib/scrollRestore";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Building2, Contact, Eye, ArrowRight, ChevronRight, Upload, Download, Loader2, Phone, Mail, MessageCircle, MoreHorizontal } from "@/components/icons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { telHref, waHref, mailHref } from "@/lib/contactActions";

// PEDIDO_EMERGENT_2A_UMA_PALAVRA: só o texto visível muda; valores continuam
// os mesmos no banco ("Ativo"/"Inativo"/"Potencial").
const STATUS_LABEL = { Potencial: "Prospect", Ativo: "Cliente", Inativo: "Cliente inativo" };
const statusColor = (s) =>
  ({ Ativo: "bg-ok-soft text-ok", Inativo: "bg-slate-100 text-slate-600", Potencial: "bg-warn-soft text-warn" }[s] || "bg-slate-100 text-slate-600");

export default function Clientes() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const canImportar = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [ampliar, setAmpliar] = useState(null);  // LOTE X-1 F2: {id, nome}
  // LOTE X-1 F2.1 — picker no topo e na tabela desktop.
  // LOTE X-1 F2.2 — backend GET /clientes ignora mine/status/q; filtra na tela.
  const [ampliarPicker, setAmpliarPicker] = useState(false);
  const [ampliarBusca, setAmpliarBusca] = useState("");
  const [ampliarLista, setAmpliarLista] = useState([]);
  const [ampliarLoading, setAmpliarLoading] = useState(false);
  useEffect(() => {
    if (!ampliarPicker) return;
    let cancelled = false;
    setAmpliarLoading(true);
    api.get("/clientes")
      .then((r) => { if (!cancelled) setAmpliarLista(r.data || []); })
      .catch(() => { if (!cancelled) setAmpliarLista([]); })
      .finally(() => { if (!cancelled) setAmpliarLoading(false); });
    return () => { cancelled = true; };
  }, [ampliarPicker]);
  // Filtro inline (sem helper, por decisão da F2.2):
  // 1) só Ativos do próprio usuário (owner_id === user.id);
  // 2) busca local por nome, razão social e CNPJ (com ou sem pontuação).
  const ampliarFiltrada = useMemo(() => {
    const uid = user?.id;
    const base = (ampliarLista || []).filter((c) => c.status === "Ativo" && c.owner_id === uid);
    const q = (ampliarBusca || "").trim().toLowerCase();
    if (!q) return base;
    const qDig = q.replace(/\D/g, "");
    return base.filter((c) => {
      const nome = (c.nome || "").toLowerCase();
      const emp = (c.empresa || c.razao_social || "").toLowerCase();
      const fant = (c.nome_fantasia || "").toLowerCase();
      const cnpjDig = (c.cnpj || "").replace(/\D/g, "");
      if (nome.includes(q) || emp.includes(q) || fant.includes(q)) return true;
      if (qDig && cnpjDig.includes(qDig)) return true;
      return false;
    });
  }, [ampliarLista, ampliarBusca, user?.id]);
  const [importBusy, setImportBusy] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const scopeId = sp.get("scope_id") || "";
  const criadoMes = sp.get("criado_mes") || "";
  const estado = sp.get("estado") || "ativos"; // PROSP-1: ativos|encerrados|lixeira
  // Propaga os filtros de escopo para o backend — quando presentes vindos do
  // drill-down da Operação, a lista já é restrita à hierarquia do card.
  const fetchQs = new URLSearchParams();
  if (scopeId) fetchQs.set("scope_id", scopeId);
  if (criadoMes) fetchQs.set("criado_mes", criadoMes);
  if (estado && estado !== "ativos") fetchQs.set("estado", estado);
  const fetchUrl = fetchQs.toString() ? `/clientes?${fetchQs.toString()}` : "/clientes";
  const { data, loading, error, reload } = useFetch(fetchUrl);

  const q = sp.get("q") || "";
  const fStatus = sp.get("status") || "";
  const fOrigem = sp.get("origem") || "";
  const fOwner = sp.get("owner") || "";

  const updateParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next, { replace: true });
  };

  const owners = useMemo(() => [...new Set((data || []).map((c) => c.owner_name).filter(Boolean))].sort(), [data]);
  const origens = useMemo(() => [...new Set((data || []).map((c) => c.origem).filter(Boolean))].sort(), [data]);

  // Novas captações (Auditoria v4, Parte 4.2) — seção fixa no topo apenas
  // quando existir pelo menos uma captação não vista no escopo. Os campos
  // Status=Potencial continuam aparecendo normalmente na lista principal.
  const captacoes = useFetch("/captacoes");
  const novasCaptacoes = ((captacoes.data?.items) || []).filter((c) => !c.visto);
  const marcarVisto = async (cid) => {
    try {
      await api.patch(`/captacoes/${cid}/visto`);
      captacoes.reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  // PROSP-1 — reativar (encerrados) / restaurar (lixeira).
  const reativarRow = async (e, cid) => {
    e.stopPropagation();
    try {
      await api.post(`/clientes/${cid}/reativar`);
      toast.success("Prospect reativado");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const restaurarRow = async (e, cid) => {
    e.stopPropagation();
    try {
      await api.post(`/clientes/${cid}/restaurar`);
      toast.success("Prospect restaurado da lixeira");
      reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const canRestore = ["admin", "director", "manager", "diretor_geral"].includes(user?.role);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data || []).filter((c) => {
      if (t && !(
        (c.nome || "").toLowerCase().includes(t) ||
        (c.nome_fantasia || "").toLowerCase().includes(t) ||
        (c.razao_social || "").toLowerCase().includes(t) ||
        (c.empresa || "").toLowerCase().includes(t) ||
        (c.cnpj || "").includes(t) ||
        (c.telefone || "").includes(t)
      )) return false;
      // PROSP-1 — nos modos encerrados/lixeira o backend já restringe; só busca local.
      if (estado !== "ativos") {
        if (fOrigem && c.origem !== fOrigem) return false;
        if (fOwner && c.owner_name !== fOwner) return false;
        return true;
      }
      // LOTE CG-1 G2 — "Perdidos" é filtro calculado: status Potencial +
      // última oportunidade fechada = "Perdido" + sem oportunidade aberta.
      const isPerdido = (c.status || "Potencial") !== "Ativo"
        && !c.has_open_deal && c.last_closed_stage === "Perdido";
      if (fStatus === "Perdido") {
        if (!isPerdido) return false;
      } else if (fStatus === "Potencial") {
        if ((c.status || "Ativo") !== "Potencial" || isPerdido) return false;
      } else if (fStatus) {
        if ((c.status || "Ativo") !== fStatus) return false;
      }
      if (fOrigem && c.origem !== fOrigem) return false;
      if (fOwner && c.owner_name !== fOwner) return false;
      return true;
    });
  }, [data, q, fStatus, fOrigem, fOwner, estado]);

  // LOTE CG-1 G2 — contagens por aba (ignoram só o filtro de SITUAÇÃO).
  const abaCounts = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = (data || []).filter((c) => {
      if (t && !(
        (c.nome || "").toLowerCase().includes(t) ||
        (c.nome_fantasia || "").toLowerCase().includes(t) ||
        (c.razao_social || "").toLowerCase().includes(t) ||
        (c.empresa || "").toLowerCase().includes(t) ||
        (c.cnpj || "").includes(t) ||
        (c.telefone || "").includes(t)
      )) return false;
      if (fOrigem && c.origem !== fOrigem) return false;
      if (fOwner && c.owner_name !== fOwner) return false;
      return true;
    });
    const isPerdido = (c) => (c.status || "Potencial") !== "Ativo" && !c.has_open_deal && c.last_closed_stage === "Perdido";
    return {
      todos: base.length,
      prospects: base.filter((c) => (c.status || "") === "Potencial" && !isPerdido(c)).length,
      clientes: base.filter((c) => (c.status || "") === "Ativo").length,
      perdidos: base.filter(isPerdido).length,
      inativos: base.filter((c) => (c.status || "") === "Inativo").length,
    };
  }, [data, q, fOrigem, fOwner]);

  useScrollRestore(!loading && !error);

  const openDetail = (id) => {
    saveCurrentScroll();
    navigate(`/cliente/${id}`);
  };

  // Template CSV com cabeçalhos e uma linha de exemplo — baixa direto pelo
  // navegador sem ida ao backend (mantém o payload da importação leve).
  const HEADERS_CSV = ["nome", "cnpj", "segmento", "telefone", "email", "cidade", "uf", "empresa", "endereco", "origem", "status", "notas"];
  const baixarTemplate = () => {
    const linha = [
      "Cliente Exemplo Ltda", "11.222.333/0001-81", "Hospital / Saúde",
      "(11) 99999-0000", "contato@exemplo.com.br", "São Paulo", "SP",
      "Cliente Exemplo Ltda", "Av. Paulista, 1000 - Bela Vista", "Prospecção",
      "Ativo", "Cliente estratégico",
    ];
    const csv = HEADERS_CSV.join(";") + "\n" + linha.join(";") + "\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "template_clientes_mpires.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Parser CSV mínimo (aceita ; ou , como separador; entende campos entre aspas
  // e ignora BOM). Não usamos lib externa para manter o bundle enxuto.
  const parseCsv = (text) => {
    const clean = text.replace(/^\ufeff/, "").trim();
    if (!clean) return { headers: [], rows: [] };
    const linhas = clean.split(/\r?\n/);
    const detectSep = (l) => (l.split(";").length > l.split(",").length ? ";" : ",");
    const sep = detectSep(linhas[0]);
    const splitLinha = (l) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < l.length; i++) {
        const c = l[i];
        if (c === '"') { if (inQ && l[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
        else if (c === sep && !inQ) { out.push(cur); cur = ""; }
        else cur += c;
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = splitLinha(linhas[0]).map((h) => h.toLowerCase());
    const rows = linhas.slice(1).filter((l) => l.trim()).map((l) => {
      const cols = splitLinha(l);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cols[i] || ""; });
      return obj;
    });
    return { headers, rows };
  };

  const submitImport = async () => {
    if (!importFile) { toast.error("Selecione um arquivo CSV"); return; }
    setImportBusy(true); setImportReport(null);
    try {
      const text = await importFile.text();
      const { rows } = parseCsv(text);
      if (!rows.length) { toast.error("CSV vazio"); return; }
      const { data: r } = await api.post("/clientes/importar", { clientes: rows });
      setImportReport(r);
      if (r.importados > 0) toast.success(`${r.importados === 1 ? "1 cliente importado" : `${r.importados} clientes importados`}`);
      if (r.importados > 0) reload();
    } catch (err) { toast.error(errMsg(err, "Falha ao importar")); }
    finally { setImportBusy(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  return (
    <div>
      <PageHeader
        title="Prospects e clientes"
        subtitle={`${(data || []).length === 1 ? "1 registro" : `${(data || []).length} registros`} na carteira`}
        action={
          <div className="flex items-center gap-2">
            {/* LOTE UX-2B item 4: desktop mantém os 3 inline; mobile deixa
                só "+ Novo prospect" e esconde "Importar CSV" / "Ampliar mix"
                num menu "⋯" para a barra caber na tela do iPhone/Android. */}
            {canImportar && (
              <button onClick={() => { setImportOpen(true); setImportFile(null); setImportReport(null); }} data-testid="import-csv-btn"
                className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-lg border border-input text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Upload className="w-4 h-4" /> Importar CSV
              </button>
            )}
            <button onClick={() => setAmpliarPicker(true)} data-testid="clientes-ampliar-mix-top"
              className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-lg border border-input text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Plus className="w-4 h-4" /> Ampliar mix
            </button>
            <button onClick={() => navigate("/captar")} data-testid="new-cliente-btn"
              className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors">
              <Plus className="w-4 h-4" /> Novo prospect
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="clientes-more-mobile" aria-label="Mais ações"
                  className="sm:hidden flex items-center justify-center h-10 w-10 rounded-lg border border-input text-slate-600 hover:bg-slate-50">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canImportar && (
                  <DropdownMenuItem onSelect={() => { setImportOpen(true); setImportFile(null); setImportReport(null); }} data-testid="clientes-more-import">
                    <Upload className="w-3.5 h-3.5 mr-2" /> Importar CSV
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setAmpliarPicker(true)} data-testid="clientes-more-ampliar">
                  <Plus className="w-3.5 h-3.5 mr-2" /> Ampliar mix
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {novasCaptacoes.length > 0 ? (
        <section className="mb-6 rounded-2xl border border-brand/30 bg-brand-soft/40 overflow-hidden" data-testid="novas-captacoes">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-brand/20">
            <Contact className="w-4 h-4 text-brand" />
            <div className="text-sm font-semibold text-navy flex-1">
              Novos prospects <span className="text-brand">({novasCaptacoes.length})</span>
            </div>
            <button
              onClick={() => navigate("/potenciais")}
              className="text-xs font-semibold text-brand hover:text-brand-hover flex items-center gap-1"
              data-testid="ver-todas-captacoes"
            >
              Ver todos <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {novasCaptacoes.slice(0, 6).map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-black/5 overflow-hidden" data-testid={`nova-captacao-${c.id}`}>
                {c.foto_id ? (
                  <div className="aspect-video bg-slate-100">
                    <img src={fileUrl(c.foto_id)} alt={c.nome} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : null}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{clienteDisplayName(c)}</div>
                      {clienteSecondaryName(c) && (
                        <div className="text-[11px] text-muted-foreground truncate">{clienteSecondaryName(c)}</div>
                      )}
                      <div className="text-[11px] text-muted-foreground truncate">Por {c.enviado_por_nome} · {fmtDateTime(c.created_at)}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase bg-brand text-white px-2 py-0.5 rounded-full shrink-0">Novo</span>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <button
                      onClick={() => marcarVisto(c.id)}
                      data-testid={`marcar-visto-${c.id}`}
                      className="flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-navy px-2 py-1 rounded"
                    >
                      <Eye className="w-3 h-3" /> Visto
                    </button>
                    {c.deal_id ? (
                      <button
                        onClick={() => navigate(`/deal/${c.deal_id}`)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-brand hover:text-brand-hover px-2 py-1 rounded"
                      >
                        Abrir <ArrowRight className="w-3 h-3" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {/* PROSP-1 — abas de ciclo de vida (ativos/encerrados/lixeira) */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="clientes-tabs-estado">
        {[
          { k: "ativos", label: "Ativos" },
          { k: "encerrados", label: "Encerrados" },
          ...(["admin", "director", "manager", "diretor_geral"].includes(user?.role)
            ? [{ k: "lixeira", label: "Lixeira (30d)" }]
            : []),
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => updateParam("estado", t.k === "ativos" ? "" : t.k)}
            data-testid={`clientes-estado-${t.k}`}
            className={`h-9 px-3.5 rounded-full text-xs font-semibold border transition-colors ${
              estado === t.k
                ? "bg-navy text-white border-navy"
                : "bg-white text-slate-600 border-input hover:bg-slate-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* PEDIDO_EMERGENT_2A: filtro de situação no topo. Padrão "Todos".
          LOTE CG-1 G2: adiciona "Perdidos" (calculado) e contagem por aba. */}
      {estado === "ativos" && (
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="clientes-tabs-situacao">
        {[
          { key: "", label: "Todos", cnt: abaCounts.todos },
          { key: "Potencial", label: "Prospects", cnt: abaCounts.prospects },
          { key: "Ativo", label: "Clientes", cnt: abaCounts.clientes },
          { key: "Perdido", label: "Perdidos", cnt: abaCounts.perdidos },
          { key: "Inativo", label: "Inativos", cnt: abaCounts.inativos },
        ].map((t) => (
          <button
            key={t.key || "all"}
            onClick={() => updateParam("status", t.key)}
            data-testid={`clientes-tab-${t.key || "todos"}`}
            className={`h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${
              (fStatus || "") === t.key
                ? "bg-brand text-white border-brand"
                : "bg-white text-slate-600 border-input hover:bg-slate-50"
            }`}
          >
            {t.label} <span className="opacity-80">({t.cnt})</span>
          </button>
        ))}
      </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => updateParam("q", e.target.value)} placeholder="Buscar por nome, empresa, CNPJ..." data-testid="clientes-search"
            className="w-full h-11 pl-10 pr-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition" />
        </div>
        {origens.length > 0 && (
          <select value={fOrigem} onChange={(e) => updateParam("origem", e.target.value)} data-testid="cli-filter-origem"
            className="h-11 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Todas origens</option>
            {origens.map((o) => <option key={o}>{o}</option>)}
          </select>
        )}
        {owners.length > 1 && (
          <select value={fOwner} onChange={(e) => updateParam("owner", e.target.value)} data-testid="cli-filter-owner"
            className="h-11 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
            <option value="">Todos responsáveis</option>
            {owners.map((o) => <option key={o}>{o}</option>)}
          </select>
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState icon={Building2} title="Nenhum registro encontrado" subtitle="Cadastre um novo prospect ou ajuste a busca." />
      ) : (
        <div className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
          {/* Desktop table */}
          <table className="w-full hidden md:table">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground border-b border-black/5">
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3">Contato</th>
                <th className="px-5 py-3">CNPJ</th>
                <th className="px-5 py-3">Responsável</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">{fStatus === "Ativo" ? "Cliente desde" : fStatus === "Perdido" ? "Perdido em" : "Criado"}</th>
                <th className="px-5 py-3 w-0"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} onClick={() => openDetail(c.id)} data-testid={`cliente-row-${c.id}`}
                  className="border-b border-black/5 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={clienteDisplayName(c)} size={34} />
                      <div>
                        <div className="font-medium text-navy">{clienteDisplayName(c)}</div>
                        {clienteSecondaryName(c)
                          ? <div className="text-xs text-muted-foreground">{clienteSecondaryName(c)}</div>
                          : (c.decisor_nome || c.empresa) && <div className="text-xs text-muted-foreground">{c.decisor_nome || c.empresa}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-600">{c.telefone || c.email || "—"}</td>
                  <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">{c.cnpj || "—"}</td>
                  <td className="px-5 py-3 text-sm text-slate-600 max-w-[160px] truncate" title={c.owner_name || ""}>{c.owner_name || "—"}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full inline-block whitespace-nowrap ${statusColor(c.status)}`}>{STATUS_LABEL[c.status || "Ativo"] || (c.status || "Cliente")}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-500">
                    {fStatus === "Ativo" && c.cliente_desde
                      ? <span title={`Cliente desde ${fmtDate(c.cliente_desde)}`}>{fmtDate(c.cliente_desde)}</span>
                      : fStatus === "Perdido" && c.last_closed_at
                        ? <span title={`Perdido em ${fmtDate(c.last_closed_at)}`}>{fmtDate(c.last_closed_at)}</span>
                        : fmtDate(c.created_at)}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-right">
                    {estado === "encerrados" ? (
                      <button
                        type="button"
                        onClick={(e) => reativarRow(e, c.id)}
                        data-testid={`cliente-reativar-${c.id}`}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                      >
                        Reativar
                      </button>
                    ) : estado === "lixeira" && canRestore ? (
                      <button
                        type="button"
                        onClick={(e) => restaurarRow(e, c.id)}
                        data-testid={`cliente-restaurar-${c.id}`}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                      >
                        Restaurar
                      </button>
                    ) : (c.status === "Ativo" && c.owner_id === user?.id && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setAmpliar({ id: c.id, nome: clienteDisplayName(c), secondary: clienteSecondaryName(c) }); }}
                        data-testid={`cliente-ampliar-mix-desk-${c.id}`}
                        className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                      >
                        Ampliar mix
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-black/5">
            {list.map((c) => {
              // Item 3.4 — dias sem contato com cor conforme regra (7/15).
              let semContato = "Sem interação recente";
              let semContatoCls = "text-muted-foreground";
              if (c.nunca_contatado) { semContato = "Nunca contatado"; semContatoCls = "text-bad font-medium"; }
              else if (typeof c.dias_sem_interacao === "number") {
                const d = c.dias_sem_interacao;
                semContato = d === 0 ? "Contato hoje" : `Sem contato há ${d} ${d === 1 ? "dia" : "dias"}`;
                if (d >= 15) semContatoCls = "text-bad font-medium";
                else if (d >= 7) semContatoCls = "text-warn font-medium";
                else semContatoCls = "text-muted-foreground";
              }
              const tel = telHref(c.telefone);
              const wa = waHref(c.telefone);
              const mail = mailHref(c.email);
              const stop = (e) => e.stopPropagation();
              return (
                <div key={c.id} onClick={() => openDetail(c.id)} data-testid={`cliente-card-${c.id}`} className="p-4 flex items-start gap-3 active:bg-slate-50">
                  <Avatar name={clienteDisplayName(c)} size={40} />
                  <div className="flex-1 min-w-0">
                    {/* Item 3.8 — nomes em até 2 linhas antes de truncar. */}
                    <div className="font-medium text-navy text-[15px] leading-snug line-clamp-2 break-words">{clienteDisplayName(c)}</div>
                    {clienteSecondaryName(c) && (
                      <div className="text-[12px] text-muted-foreground truncate">{clienteSecondaryName(c)}</div>
                    )}
                    <div className={`text-xs mt-0.5 ${semContatoCls}`} data-testid={`cliente-sem-contato-${c.id}`}>{semContato}</div>
                    <div className="flex items-center gap-1 mt-1.5" onClick={stop}>
                      {tel && <a href={tel} data-testid={`cliente-tel-${c.id}`} aria-label="Ligar" className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-brand hover:bg-brand-soft"><Phone className="w-4 h-4" /></a>}
                      {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid={`cliente-wa-${c.id}`} aria-label="WhatsApp" className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-[#25D366] hover:bg-[#25D366]/10"><MessageCircle className="w-4 h-4" /></a>}
                      {mail && <a href={mail} data-testid={`cliente-mail-${c.id}`} aria-label="E-mail" className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-navy hover:bg-slate-200"><Mail className="w-4 h-4" /></a>}
                      {estado === "encerrados" ? (
                        <button
                          type="button"
                          onClick={(e) => reativarRow(e, c.id)}
                          data-testid={`cliente-reativar-m-${c.id}`}
                          className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                        >
                          Reativar
                        </button>
                      ) : estado === "lixeira" && canRestore ? (
                        <button
                          type="button"
                          onClick={(e) => restaurarRow(e, c.id)}
                          data-testid={`cliente-restaurar-m-${c.id}`}
                          className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                        >
                          Restaurar
                        </button>
                      ) : (c.status === "Ativo" && c.owner_id === user?.id && (
                        <button
                          type="button"
                          onClick={() => setAmpliar({ id: c.id, nome: clienteDisplayName(c), secondary: clienteSecondaryName(c) })}
                          data-testid={`cliente-ampliar-mix-${c.id}`}
                          className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-soft text-brand-hover hover:bg-brand hover:text-white transition-colors"
                        >
                          Ampliar mix
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${statusColor(c.status)}`}>{STATUS_LABEL[c.status || "Ativo"] || (c.status || "Cliente")}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diálogo de importação CSV — admin/gestores */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar clientes via CSV</DialogTitle>
            <DialogDescription>
              Dedup por CNPJ contra a base inteira. Linhas inválidas ficam no relatório sem parar o lote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button type="button" onClick={baixarTemplate} data-testid="csv-template-btn"
              className="w-full flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-brand/40 text-brand-hover text-sm font-medium hover:bg-brand-soft/40">
              <Download className="w-4 h-4" /> Baixar template (.csv)
            </button>
            <div className="text-[11px] text-muted-foreground -mt-1">
              Cabeçalhos: <code className="text-slate-700">nome; cnpj; segmento; telefone; email; cidade; uf; empresa; endereco; origem; status; notas</code>. Separador ; ou , aceitos. Obrigatórios: <b>nome</b>, <b>cnpj</b>, <b>segmento</b>.
            </div>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              data-testid="csv-file-input"
              className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-brand-soft file:text-brand-hover file:text-sm file:font-medium hover:file:brightness-95" />
            {importReport && (
              <div data-testid="csv-report" className="rounded-lg border border-black/5 bg-slate-50 p-3 text-sm">
                <div><b>{importReport.importados}</b> {importReport.importados === 1 ? "importado" : "importados"} · <b>{importReport.falharam}</b> {importReport.falharam === 1 ? "falha" : "falhas"} de <b>{importReport.total}</b> {importReport.total === 1 ? "linha" : "linhas"}.</div>
                {importReport.falhas?.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs">
                    {importReport.falhas.slice(0, 30).map((f, i) => (
                      <li key={i} className="text-bad">Linha {f.linha} — {f.nome || "(sem nome)"} · {f.motivo}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setImportOpen(false)} className="h-10 px-4 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">Fechar</button>
            <button type="button" onClick={submitImport} disabled={importBusy || !importFile} data-testid="csv-submit"
              className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
              {importBusy && <Loader2 className="w-4 h-4 animate-spin" />} Importar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={ampliarPicker} onOpenChange={setAmpliarPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escolha o cliente para ampliar o mix</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="ampliar-mix-picker">
            <input
              value={ampliarBusca}
              onChange={(e) => setAmpliarBusca(e.target.value)}
              placeholder="Buscar cliente ativo..."
              data-testid="ampliar-mix-picker-busca"
              className="w-full h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
              autoFocus
            />
            <div className="max-h-72 overflow-y-auto border border-input rounded-lg">
              {ampliarLoading ? (
                <div className="p-4 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Carregando…</div>
              ) : ampliarFiltrada.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground" data-testid="ampliar-mix-picker-vazio">
                  {ampliarBusca ? "Nenhum cliente ativo encontrado." : "Você não tem clientes ativos na sua carteira."}
                </div>
              ) : (
                ampliarFiltrada.slice(0, 50).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setAmpliar({ id: c.id, nome: clienteDisplayName(c), secondary: clienteSecondaryName(c) }); setAmpliarPicker(false); }}
                    data-testid={`ampliar-mix-picker-item-${c.id}`}
                    className="w-full text-left px-3 py-2.5 border-b last:border-b-0 border-slate-100 hover:bg-brand-soft/40"
                  >
                    <div className="text-sm font-medium text-navy truncate">{clienteDisplayName(c)}</div>
                    {clienteSecondaryName(c) && (
                      <div className="text-[11px] text-muted-foreground truncate">{clienteSecondaryName(c)}</div>
                    )}
                    {c.cidade && <div className="text-xs text-muted-foreground">{c.cidade}</div>}
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AmpliarMixModal
        open={!!ampliar}
        onOpenChange={(v) => { if (!v) setAmpliar(null); }}
        cliente={ampliar || { id: "", nome: "" }}
      />
    </div>
  );
}
